import { useCallback, useState } from 'react';

import { useSession } from '@/compositions/hooks/useSession';
import { isSnoozed, ONE_DAY_MS } from '@/utils/helpers/snooze';
import { createStorage } from '@/utils/helpers/storage';

interface SignUpReminderSnooze {
  /** De qual conta anônima é a dispensa. */
  userId: string;
  snoozedAt: number;
}

/**
 * Fora do hook: a chave é a mesma para o app inteiro, e criar o acessor a cada
 * montagem só produziria objetos novos apontando para ela.
 */
const storage = createStorage<SignUpReminderSnooze | null>('signup-reminder-snooze', null);

/** O que foi gravado ainda tem a forma esperada? Valor de versão antiga não adia nada. */
function snoozedAtFor(stored: SignUpReminderSnooze | null, userId: string | null): number | null {
  if (!stored || typeof stored.snoozedAt !== 'number') return null;

  return stored.userId === userId ? stored.snoozedAt : null;
}

/**
 * O convite para terminar o cadastro de quem pediu socorro sem conta (DV-24).
 *
 * A sessão anônima fica guardada no aparelho, e era só isso: ao reabrir o app,
 * nada dizia à pessoa que o pedido dela está preso a uma conta que some se o
 * navegador limpar os dados. O aviso aparece **enquanto** a sessão for anônima
 * e some sozinho quando o cadastro termina — `isAnonymous` cai e o
 * `invalidateQueries` da sessão já acontece no fim do fluxo.
 *
 * ## Dispensável, mas por um dia
 *
 * Fechar adia o aviso por 24 horas, e não para sempre: quem fechou sem ler é
 * exatamente quem perderia o acompanhamento. Um dia é o ritmo de quem volta ao
 * app para ver se o resgate andou — reaparecer a cada abertura viraria ruído, e
 * sumir de vez deixaria a conta anônima para trás.
 *
 * A dispensa vale para **uma** conta anônima (`userId`): se a pessoa pedir
 * socorro de novo depois de perder a sessão, a conta é outra, e o aviso volta.
 *
 * `localStorage` e não servidor, como em `useOperationNoticeDismissal`: é
 * conveniência de tela num aparelho. Se o storage falhar (modo privativo), o
 * aviso só não lembra a dispensa — `createStorage` engole o erro.
 *
 * ## O relógio é lido uma vez por montagem
 *
 * `now` vem do inicializador do `useState`, e não de `Date.now()` no render: o
 * render precisa ser puro, e o `AppLayout` monta uma vez por abertura do app.
 * Na prática o prazo é conferido a cada abertura — o que basta, porque é ao
 * reabrir que a pessoa precisa do lembrete.
 */
export function useSignUpReminder() {
  const { session, isAnonymous } = useSession();
  const userId = session?.userId ?? null;

  // Lidos uma vez por montagem (ver acima): a dispensa gravada e o relógio.
  const [stored] = useState(() => storage.read());
  const [now] = useState(() => Date.now());
  // A dispensa desta abertura não passa pelo prazo: o instante gravado é
  // posterior ao `now` da montagem, e `isSnoozed` trata instante no futuro como
  // relógio fora do lugar — o aviso não sumiria no toque.
  const [isDismissedNow, setIsDismissedNow] = useState(false);

  const isVisible =
    isAnonymous && !isDismissedNow && !isSnoozed(snoozedAtFor(stored, userId), now, ONE_DAY_MS);

  const dismiss = useCallback(() => {
    setIsDismissedNow(true);

    if (!userId) return;

    // O instante gravado é o do toque, e não o `now` da montagem: com o app
    // aberto há horas, gravar o da montagem encurtaria o adiamento.
    storage.write({ userId, snoozedAt: Date.now() });
  }, [userId]);

  return { isVisible, dismiss };
}
